import React, { useState, useRef } from 'react';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaArrowLeft, FaHeading, FaImage } from 'react-icons/fa';
import { compressImageToDataUrl } from '../utils/imageCompressor';
import '../pages_premium.css';
import './cars/AddCar.css';

const MAX_PHOTOS = 3;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

function AddBlog() {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [activeField, setActiveField] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        status: 'published',
        images: []
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const remainingSlots = MAX_PHOTOS - formData.images.length;
        if (remainingSlots <= 0) {
            toast.error(`Only ${MAX_PHOTOS} photos allowed.`);
            e.target.value = '';
            return;
        }

        const oversized = files.find(f => f.size > MAX_FILE_SIZE_BYTES);
        if (oversized) {
            toast.error(`"${oversized.name}" exceeds the ${MAX_FILE_SIZE_MB} MB limit.`);
            e.target.value = '';
            return;
        }

        let filesToProcess = files;
        if (files.length > remainingSlots) {
            toast.error(`Only ${MAX_PHOTOS} photos allowed total. Added first ${remainingSlots} photo(s).`);
            filesToProcess = files.slice(0, remainingSlots);
        }

        try {
            const compressed = await Promise.all(
                filesToProcess.map(file => compressImageToDataUrl(file))
            );
            setFormData(prev => ({
                ...prev,
                images: [...prev.images, ...compressed].slice(0, MAX_PHOTOS)
            }));
        } catch (err) {
            console.error('Image compression failed:', err);
        }
        e.target.value = '';
    };

    const handleRemoveImage = (index) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, idx) => idx !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title.trim()) {
            return toast.error('Please enter a blog title');
        }
        if (!formData.content.trim()) {
            return toast.error('Please enter the blog content');
        }

        setLoading(true);
        try {
            await axiosInstance.post('/blogs', formData);
            toast.success('✍️ Blog post created successfully!');
            navigate('/blogs');
        } catch (error) {
            console.error('Error creating blog:', error);
            toast.error(error.response?.data?.message || 'Failed to create blog post');
        } finally {
            setLoading(false);
        }
    };

    const filledPercentage = Object.values(formData).filter(v => Array.isArray(v) ? v.length > 0 : !!v).length / 4 * 100;

    return (
        <div className="page-premium-wrapper">
            <div className="page-premium-content">

                {/* Hero Header */}
                <div className="page-hero-header page-hero-properties" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                    <div className="ph-blob ph-blob-1"></div>
                    <div className="ph-blob ph-blob-2"></div>

                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaEdit size={28} />
                        </div>
                        <div>
                            <h1 className="ph-title">Create Blog Post</h1>
                            <p className="ph-subtitle">Publish a new article, update, or student guide for the website</p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{Math.round(filledPercentage)}%</span>
                            <span className="count-lbl">Filled</span>
                        </div>
                    </div>
                </div>

                {/* Back button */}
                <div className="mb-3">
                    <button type="button" className="ac-back-btn" onClick={() => navigate('/blogs')}>
                        <FaArrowLeft /> Back to Blogs
                    </button>
                </div>

                {/* Main Form Card */}
                <div className="ac-form-card">
                    <form onSubmit={handleSubmit}>

                        {/* SECTION 1: Details */}
                        <div className="ac-section">
                            <div className="ac-section-header">
                                <div className="ac-section-icon"><FaHeading /></div>
                                <div>
                                    <h2>Blog Information</h2>
                                    <p>Add title, content body, and publishing settings</p>
                                </div>
                            </div>

                            <div className="ac-field-group">
                                <div className={`ac-field-wrap ac-field-full ${activeField === 'title' ? 'ac-field-active' : ''} ${formData.title ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="blogTitle">Blog Title *</label>
                                    <input 
                                        type="text" 
                                        id="blogTitle"
                                        name="title" 
                                        value={formData.title} 
                                        onChange={handleChange} 
                                        onFocus={() => setActiveField('title')}
                                        onBlur={() => setActiveField(null)}
                                        placeholder="e.g. Living Cost in Australia: Guide for Students 2026"
                                        required 
                                    />
                                </div>

                                <div className={`ac-field-wrap ${activeField === 'status' ? 'ac-field-active' : ''} ${formData.status ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="blogStatus">Status *</label>
                                    <select 
                                        id="blogStatus"
                                        name="status" 
                                        value={formData.status} 
                                        onChange={handleChange}
                                        onFocus={() => setActiveField('status')}
                                        onBlur={() => setActiveField(null)}
                                    >
                                        <option value="published">Published</option>
                                        <option value="draft">Draft</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="ac-divider"></div>

                        {/* SECTION 2: Image upload */}
                        <div className="ac-section">
                            <div className="ac-section-header">
                                <div className="ac-section-icon"><FaImage /></div>
                                <div>
                                    <h2>Cover / Images</h2>
                                    <p>Select thumbnail photos to display on the blog card</p>
                                </div>
                            </div>

                            <div className="ac-field-group ac-field-full px-3">
                                <input 
                                    type="file" 
                                    id="blogImageInput"
                                    ref={fileInputRef} 
                                    onChange={handleFileChange}
                                    multiple 
                                    accept="image/*"
                                    className="d-none"
                                />
                                <button 
                                    type="button"
                                    onClick={() => fileInputRef.current.click()}
                                    className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:bg-light transition cursor-pointer mb-3 w-100"
                                    style={{ borderStyle: 'dashed', background: 'none' }}
                                >
                                    <span className="m-0 text-secondary font-weight-bold">Click here to upload cover image</span>
                                </button>

                                {formData.images.length > 0 && (
                                    <div className="d-flex flex-wrap gap-2">
                                        {formData.images.map((imgUrl, idx) => (
                                            <div key={idx} className="position-relative rounded-lg overflow-hidden border shadow-sm" style={{ width: '120px', height: '80px' }}>
                                                <img src={imgUrl} alt="preview" className="w-full h-full object-cover" />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveImage(idx)}
                                                    className="position-absolute btn btn-danger btn-sm p-1 rounded-circle border-0"
                                                    style={{ top: '4px', right: '4px', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="ac-divider"></div>

                        {/* SECTION 3: Content Editor */}
                        <div className="ac-section">
                            <div className="ac-section-header">
                                <div className="ac-section-icon"><FaEdit /></div>
                                <div>
                                    <h2>Article Body Content</h2>
                                    <p>Write your detailed guide article here</p>
                                </div>
                            </div>

                            <div className="ac-field-group">
                                <div className={`ac-field-wrap ac-field-full ${activeField === 'content' ? 'ac-field-active' : ''} ${formData.content ? 'ac-field-filled' : ''}`}>
                                    <label htmlFor="blogContent">Content Body *</label>
                                    <textarea 
                                        id="blogContent"
                                        name="content" 
                                        rows="12"
                                        value={formData.content} 
                                        onChange={handleChange}
                                        onFocus={() => setActiveField('content')}
                                        onBlur={() => setActiveField(null)}
                                        placeholder="Start writing the blog post body..."
                                        required 
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Form Submit Footer */}
                        <div className="ac-form-footer">
                            <button type="button" className="ac-cancel-btn" onClick={() => navigate('/blogs')}>
                                Cancel
                            </button>
                            <button type="submit" className="ac-submit-btn ac-submit-ready" disabled={loading}>
                                {loading ? 'Saving Post...' : 'Save & Publish'}
                            </button>
                        </div>

                    </form>
                </div>

            </div>
        </div>
    );
}

export default AddBlog;
